import { useAuthContext } from '@offer/auth-lib'
import { VpChevronLeftIcon, VpChevronRightIcon } from '@vtmn-play/icons/react'
import { VpIconButton } from '@vtmn-play/react/headless'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import seasonsBackgroundImage from '@/assets/seasons-bg.webp'
import { AppVersion } from '@/components/AppVersion/AppVersion'
import { useSeasonCarousel } from '@/hooks'
import { useSeasons } from '@/hooks/useSeasons'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import { generateDynamicSeasons } from '@/utils/season'
import { SeasonCard } from './SeasonCard'
import { SeasonCardSkeleton } from './SeasonCardSkeleton'
import './season-selection-page.scss'

export function SeasonSelectionPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { listRef, canScrollLeft, canScrollRight, scrollBy } = useSeasonCarousel()
  const { user } = useAuthContext()

  const { data: seasonsFromAPI, isLoading, isError } = useSeasons()

  const selectSeason = async ({ season, status }: { season: string, status: string }) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.SEASON_SELECTED, { season_id: season.replace(/\s+/g, ''), season_status: status })
    await navigate(`/${season.replace(/\s+/g, '')}`)
  }

  const renderSeasonCards = () => {
    if (isLoading) {
      return Array.from({ length: 8 }, (_, i) => <SeasonCardSkeleton key={i} />)
    }

    const seasons = isError ? generateDynamicSeasons() : seasonsFromAPI
    return seasons?.map(season => (
      <SeasonCard
        key={season.season}
        season={season}
        onClick={() => selectSeason(season)}
      />
    ))
  }

  return (
    <section className="season-selection-page" data-testid="season-selection-page">
      <img
        className="season-selection-page__background"
        src={seasonsBackgroundImage}
        alt=""
        aria-hidden="true"
        fetchPriority="auto"
        decoding="async"
      />
      <p className="season-selection-page__welcome">
        {t('season-selection.welcome', { name: user?.givenName ?? user?.surname ?? '' })}
      </p>
      <h1 className="season-selection-page__title">
        {t('season-selection.select-your-season')}
      </h1>
      <div className="season-selection-page__carousel">
        <VpIconButton
          aria-label={t('season-selection.previous-seasons')}
          className={clsx('season-selection-page__arrow', { hidden: !canScrollLeft })}
          variant="ghost"
          onClick={() => scrollBy('left')}
        >
          <VpChevronLeftIcon aria-hidden="true" />
        </VpIconButton>
        <ul
          ref={listRef}
          className="season-selection-page__list"
          aria-label={t('season-selection.title')}
        >
          {renderSeasonCards()}
        </ul>
        <VpIconButton
          aria-label={t('season-selection.next-seasons')}
          className={clsx('season-selection-page__arrow', { hidden: !canScrollRight })}
          variant="ghost"
          onClick={() => scrollBy('right')}
        >
          <VpChevronRightIcon aria-hidden="true" />
        </VpIconButton>
      </div>
      <AppVersion />
    </section>
  )
}
